import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { getSessionContexts, selectSessionContext } from '../auth/AuthService';
import { destinationForRole as destinationFor } from '../auth/roleUtils';


export default function PracticeContextSwitcher() {
  const { token, user, login } = useAuth();
  const navigate = useNavigate();
  const [contexts, setContexts] = useState([]);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState('');
  const switchInFlight = useRef(false);
  const activeButton = useRef(null);
  const active = user?.contextReferenceId || '';

  useEffect(() => {
    let cancelled = false;
    setContexts([]);
    if (token && user?.contextSelection !== 'true') {
      getSessionContexts(token)
        .then((available) => { if (!cancelled) setContexts(available); })
        .catch(() => { if (!cancelled) setContexts([]); });
    }
    return () => { cancelled = true; };
  }, [token, user?.contextSelection]);

  useEffect(() => {
    const button = activeButton.current;
    if (!button) return;
    const container = button.parentElement;
    container.scrollLeft = Math.max(0, button.offsetLeft - container.offsetLeft
      - (container.clientWidth - button.offsetWidth) / 2);
  }, [active, contexts]);

  const switchContext = async (referenceId) => {
    if (!referenceId || referenceId === active || switchInFlight.current) return;
    switchInFlight.current = true;
    setSwitching(true);
    setError('');
    try {
      const response = await selectSessionContext(referenceId, token);
      login(response.token);
      navigate(destinationFor(response.userRole), { replace: true });
    } catch {
      setError('Could not switch practices. Please try again.');
    } finally {
      switchInFlight.current = false;
      setSwitching(false);
    }
  };

  // A revoked active practice can leave one valid destination. Keep that
  // destination available instead of trapping the user in the revoked session.
  if (contexts.length === 0 || (contexts.length === 1 && contexts[0].referenceId === active)) return null;

  return (
    <section aria-label="Practice switcher" className="border-b border-[#d7e1da] bg-white px-3 py-2 sm:px-6">
      <div className="flex min-w-0 items-center gap-3">
        <span className="shrink-0 text-xs font-semibold text-gray-500">Practice</span>
        <div role="group" aria-label="Practice and role" aria-busy={switching}
          className="relative flex min-w-0 flex-1 gap-1 overflow-x-auto py-1">
          {contexts.map((context) => {
            const selected = context.referenceId === active;
            const role = context.type === 'client' ? 'Client' : context.organizationRole || 'Staff';
            return (
              <button key={`${context.type}:${context.referenceId}`} type="button"
                ref={selected ? activeButton : null}
                aria-pressed={selected} disabled={switching}
                onClick={() => switchContext(context.referenceId)}
                className={`flex min-h-[44px] shrink-0 items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#4f7a64] disabled:cursor-wait disabled:opacity-60 ${
                  selected ? 'border-[#4f7a64] bg-[#edf4ef] text-[#305540]' : 'border-transparent text-gray-600 hover:border-gray-200 hover:bg-gray-50'
                }`}>
                {selected && <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-[#4f7a64]" />}
                <span className="font-semibold">{context.organizationName}</span>
                <span className={`text-xs ${selected ? 'text-[#4f7a64]' : 'text-gray-500'}`}>{role}</span>
              </button>
            );
          })}
        </div>
      </div>
      {switching && <p role="status" className="mt-1 text-xs text-gray-600">Switching practice…</p>}
      {error && <p role="alert" className="mt-1 text-sm text-red-700">{error}</p>}
    </section>
  );
}
